import { useAuth0 } from '@auth0/auth0-react';
import type { DefectListFilter, DefectStatus, QuestionSeverity } from '@my/check-api';
import { useDefect, useDefects, useRecordDefectAction } from '@my/check-api/hooks';
import { ErrorBanner, Loading, PageHeader, showToast } from '@my/ui';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { DefectDetail } from './DefectDetail';
import { DefectQueue } from './DefectQueue';
import { SEVERITY_ORDER } from './lib/defectTypes';

// ────────────────────────────────────────────────────────────────────────────
// Defects — live against /check/api/v1/defects.
//
// No create action: a defect comes from a failed check answer. The only write
// is recording an action, which is also the only way status, garage or due
// date change — so every change carries a note and an author.
//
// Filtering and ordering are server-side; see lib/defectTypes.ts for the
// modelling questions still open.
// ────────────────────────────────────────────────────────────────────────────

export function DefectListPage() {
  const { t } = useTranslation('checks');
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);

  const [severity, setSeverity] = useState<QuestionSeverity | 'all'>('all');
  const [unresolvedOnly, setUnresolvedOnly] = useState(true);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filter: DefectListFilter = useMemo(
    () => ({
      ...(severity !== 'all' ? { severity } : {}),
      ...(unresolvedOnly ? { unresolvedOnly: true } : {}),
      ...(overdueOnly ? { overdueOnly: true } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
    }),
    [severity, unresolvedOnly, overdueOnly, search],
  );

  const listQuery = useDefects(orgId, filter);
  const detailQuery = useDefect(selectedId, orgId);
  const recordMutation = useRecordDefectAction();

  const defects = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  // Keep a selection alive as filters change: select the first card when
  // nothing is chosen, and drop the selection if the chosen card filtered out.
  useEffect(() => {
    if (defects.length === 0) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) =>
      prev && defects.some((d) => d.id === prev) ? prev : (defects[0]?.id ?? null),
    );
  }, [defects]);

  const severityLabels: Record<QuestionSeverity, string> = {
    High: t('defects.severity.high'),
    Medium: t('defects.severity.medium'),
    Low: t('defects.severity.low'),
  };
  const statusLabels: Record<DefectStatus, string> = {
    Open: t('defects.status.open'),
    InProgress: t('defects.status.inProgress'),
    Deferred: t('defects.status.deferred'),
    Resolved: t('defects.status.resolved'),
  };

  const recordAction = async (input: {
    note: string;
    statusAfter: DefectStatus;
    garageName: string | null;
    dueUtc: string | null;
  }) => {
    if (!orgId || !selectedId) return;
    try {
      await recordMutation.mutateAsync({
        defectId: selectedId,
        organisationId: orgId,
        ...input,
      });
      showToast({ type: 'success', message: t('defects.record.saved') });
    } catch (err) {
      showToast({
        type: 'error',
        message:
          err instanceof Error && err.message ? err.message : t('defects.record.saveFailed'),
      });
    }
  };

  if (!orgId) {
    return (
      <Shell>
        <ErrorBanner title={t('templates.noOrg.title')} message={t('templates.noOrg.message')} />
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex flex-wrap items-center gap-2 pb-3">
        <button
          type="button"
          onClick={() => setSeverity('all')}
          className={chipClass(severity === 'all')}
        >
          {t('defects.filters.all')}
        </button>
        {SEVERITY_ORDER.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setSeverity(s)}
            className={chipClass(severity === s)}
          >
            {severityLabels[s]}
          </button>
        ))}

        <span className="h-4 w-px bg-slate-300" aria-hidden="true" />

        <button
          type="button"
          onClick={() => setUnresolvedOnly((v) => !v)}
          className={chipClass(unresolvedOnly)}
        >
          {t('defects.filters.unresolvedOnly')}
        </button>
        <button
          type="button"
          onClick={() => setOverdueOnly((v) => !v)}
          className={chipClass(overdueOnly)}
        >
          {t('defects.filters.overdueOnly')}
        </button>

        <div className="flex-1" />

        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <input
            type="text"
            value={search}
            placeholder={t('defects.search')}
            onChange={(e) => setSearch(e.target.value)}
            className="w-56 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
      </div>

      {listQuery.isPending && <Loading label={t('defects.title')} />}

      {listQuery.isError && (
        <ErrorBanner title={t('defects.loadFailed')} message={listQuery.error.message} />
      )}

      {listQuery.isSuccess && (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 pb-4 lg:grid-cols-[minmax(300px,380px)_1fr]">
          <div className="min-h-0 lg:h-full">
            <DefectQueue
              defects={defects}
              selectedId={selectedId}
              onSelect={setSelectedId}
              severityLabels={severityLabels}
              statusLabels={statusLabels}
            />
          </div>
          <div className="min-h-0 lg:h-full">
            {detailQuery.isPending && selectedId ? (
              <div className="flex h-full items-center justify-center rounded-sm border border-slate-300 bg-white shadow-sm">
                <Loading label={t('defects.title')} />
              </div>
            ) : (
              <DefectDetail
                defect={detailQuery.data ?? null}
                isSaving={recordMutation.isPending}
                severityLabels={severityLabels}
                statusLabels={statusLabels}
                onRecordAction={(input) => void recordAction(input)}
              />
            )}
          </div>
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('checks');
  return (
    <div className="flex h-full min-h-0 flex-col px-2">
      <PageHeader title={t('defects.title')} description={t('defects.description')} />
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</main>
    </div>
  );
}

function chipClass(active: boolean): string {
  return `rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors ${
    active
      ? 'border-slate-700 bg-slate-700 text-white'
      : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700'
  }`;
}
